import { MOSAIC_SIZES, type Clues, type MosaicSize } from '@hazel/shared'

/**
 * One grid, one table.
 *
 * The rule Sky's Memory Match learned the hard way and Stripey's Sudoku wrote
 * down: the number of columns the cursor steps by and the class that draws
 * them live together, and a test checks they still agree. Her board's cursor
 * kept its own idea of the width — four, always — while the CSS widened to six
 * at a breakpoint, so Down moved four cells across a six-wide row.
 *
 * A mosaic cannot reflow, for the same reason a sudoku cannot: fifteen columns
 * are fifteen columns, because fourteen columns of a fifteen-wide picture is
 * not the picture. So the cursor steps by the size and there is no breakpoint
 * in it at all.
 *
 * **What is new here is that a size can be too big for a screen.** A nonogram
 * is not only its grid — the clues live in a gutter along the top and down the
 * side, and that gutter is as wide as the longest clue. At fifteen across the
 * whole thing is twenty-three cells wide, which on a 390px phone is seventeen
 * pixels a cell with numbers inside them. So the table says which screens a
 * size may be offered on, rather than that living as a condition somewhere in
 * the markup where the reason for it would be lost.
 */

/**
 * The narrowest screen each size may be drawn on.
 *
 * Numbers rather than a media query, because this is arithmetic before it is
 * CSS: what has to fit is the grid *plus its clue gutter*, and the widest
 * gutter a size can ever want is one clue per two squares — eight for a
 * fifteen across, so twenty-three cells. Seven hundred is a small tablet in
 * portrait, and 700 ÷ 23 is 30px a square.
 *
 * The query below is built from the same number, so the width the CSS asks
 * about and the width the sums assume cannot drift apart. A test holds the
 * budget against the worst gutter each size could ever have, rather than
 * against the pictures that happen to be shipped — a picture is data and can
 * change; the ceiling on how much clue a line can hold cannot.
 */
export const NARROWEST_SCREEN: Readonly<Record<MosaicSize, number>> = {
  5: 390,
  10: 390,
  15: 700,
}

/** The smallest screen that gets everything, which is the only query needed. */
export const ROOM_FOR_THE_BIGGEST = `(min-width: ${NARROWEST_SCREEN[15]}px)`

/** The widest gutter a line of this length could ever ask for. */
export function widestGutterFor(size: MosaicSize): number {
  return Math.ceil(size / 2)
}

export interface MosaicLayout {
  /** Columns of picture. Also what the cursor steps by. */
  readonly columns: number
  readonly rows: number
  /**
   * Whether this size needs `ROOM_FOR_THE_BIGGEST` before it may be offered.
   *
   * Kept beside the columns rather than derived from `NARROWEST_SCREEN`, and
   * a test holds the two in step — the point of the table is that everything
   * about a size is readable in one place.
   */
  readonly needsRoom: boolean
  /**
   * How big a square is drawn, per grid and per scale.
   *
   * Written down per size rather than left to inherit, for the reason
   * Stripey's gives: a square is a fifteenth of the grid on the biggest and a
   * fifth on the smallest, so one size is either lost in a 5×5 or spilling out
   * of a 15×15. Not container-query units, which this repo has already been
   * caught by resolving to zero inside a box sized by `flex-grow`.
   */
  readonly squareClassName: string
  /** The clue numbers in the gutter, which have to be legible from a sofa. */
  readonly clueClassName: string
}

export const MOSAIC_LAYOUT: Readonly<Record<MosaicSize, MosaicLayout>> = {
  5: {
    columns: 5,
    rows: 5,
    needsRoom: false,
    squareClassName: 'text-2xl [[data-scale=tv]_&]:text-5xl',
    clueClassName: 'text-lg [[data-scale=tv]_&]:text-3xl',
  },
  10: {
    columns: 10,
    rows: 10,
    needsRoom: false,
    squareClassName: 'text-base [[data-scale=tv]_&]:text-3xl',
    clueClassName: 'text-sm [[data-scale=tv]_&]:text-2xl',
  },
  15: {
    columns: 15,
    rows: 15,
    needsRoom: true,
    squareClassName: 'text-xs [[data-scale=tv]_&]:text-xl',
    clueClassName: 'text-xs [[data-scale=tv]_&]:text-lg',
  },
}

export function columnsFor(size: MosaicSize): number {
  return MOSAIC_LAYOUT[size].columns
}

/** The sizes worth offering on this screen. */
export function sizesOffered(hasRoom: boolean): readonly MosaicSize[] {
  return MOSAIC_SIZES.filter((size) => hasRoom || !MOSAIC_LAYOUT[size].needsRoom)
}

/**
 * How wide the clue gutter has to be, for these clues.
 *
 * Measured from the picture rather than taken from the size, because the
 * longest clue is a fact about the picture: a 15×15 whose rows never hold more
 * than five runs does not need a gutter eight wide, and giving it one would
 * shrink every square to pay for numbers that are not there.
 *
 * At least one, so a picture whose every line is blank still has a gutter to
 * put the nothing in.
 */
export function gutterFor(clues: Clues): { across: number; down: number } {
  return {
    across: Math.max(1, ...clues.rows.map((clue) => clue.length)),
    down: Math.max(1, ...clues.columns.map((clue) => clue.length)),
  }
}

/**
 * Where a square lands after a step, or null if that would leave the picture.
 *
 * The cursor lives here rather than in the component, because "one cell in the
 * direction pressed" is what the ticket asks to be asserted and it should be
 * assertable without rendering anything.
 *
 * **Left and right do not run on to the next row**, the same as Stripey's: a
 * grid is not a line of text, and a cursor that wraps loses her place while
 * she is reading across a row against its clue.
 */
export function stepFrom(
  size: MosaicSize,
  cell: number,
  direction: 'left' | 'right' | 'up' | 'down',
): number | null {
  const { columns, rows } = MOSAIC_LAYOUT[size]
  const row = Math.floor(cell / columns)
  const column = cell % columns

  if (direction === 'left') return column > 0 ? cell - 1 : null
  if (direction === 'right') return column < columns - 1 ? cell + 1 : null
  if (direction === 'up') return row > 0 ? cell - columns : null

  return row < rows - 1 ? cell + columns : null
}

/**
 * What one press of the cycle key does to a square.
 *
 * Empty, filled, marked, and round again. Filling is first because it is what
 * she means most often when she has just worked something out, and a cycle
 * that reached "filled" second would make the commonest move the slower one.
 */
export function cycle(state: 'unknown' | 'filled' | 'marked'): 'unknown' | 'filled' | 'marked' {
  if (state === 'unknown') return 'filled'
  if (state === 'filled') return 'marked'

  return 'unknown'
}
